import React, { useState, useMemo } from 'react';
import { AlertTriangle, ShieldAlert, Clock, FileWarning, Hash, Zap, Brain } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { VesselDrawer } from '@/components/vessel/VesselDrawer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useApp } from '@/context/AppContext';
import { anomalies, getAnomaliesByType, getSeverityColor, getVesselById } from '@/data/mockData';

const AnomalyTypeIcon = ({ type }) => {
  switch (type) {
    case 'invalid_mmsi':
      return <Hash className="w-4 h-4" />;
    case 'invalid_imo':
      return <FileWarning className="w-4 h-4" />;
    case 'invalid_callsign':
      return <ShieldAlert className="w-4 h-4" />;
    case 'stay_long_time':
      return <Clock className="w-4 h-4" />;
    default:
      return <AlertTriangle className="w-4 h-4" />;
  }
};

const SeverityBadge = ({ severity }) => {
  const colorClass = getSeverityColor(severity);
  return (
    <Badge
      variant="outline"
      className={cn(
        "text-xs",
        colorClass === 'destructive' && "border-destructive text-destructive",
        colorClass === 'warning' && "border-warning text-warning",
        colorClass === 'info' && "border-info text-info"
      )}
    >
      {severity}
    </Badge>
  );
};

const AnomalyTable = ({ anomalies, onVesselClick }) => {
  const formatDateTime = (dateStr) => {
    return new Date(dateStr).toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (anomalies.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <AlertTriangle className="w-16 h-16 mb-4 opacity-30" />
        <p className="text-sm">Tidak ada anomali pada kategori ini</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-muted/50">
          <TableHead className="text-xs">Vessel Name</TableHead>
          <TableHead className="text-xs">MMSI</TableHead>
          <TableHead className="text-xs">IMO</TableHead>
          <TableHead className="text-xs">Date</TableHead>
          <TableHead className="text-xs">Reason</TableHead>
          <TableHead className="text-xs">Severity</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {anomalies.map((anomaly) => (
          <TableRow
            key={anomaly.id}
            className="table-row-interactive"
            onClick={() => {
              const vessel = getVesselById(anomaly.vesselId);
              if (vessel) onVesselClick(vessel);
            }}
          >
            <TableCell>
              <div className="flex items-center gap-2">
                <AnomalyTypeIcon type={anomaly.type} />
                <span className="text-sm font-medium">{anomaly.vesselName}</span>
              </div>
            </TableCell>
            <TableCell className="text-xs font-mono">{anomaly.mmsi}</TableCell>
            <TableCell className="text-xs font-mono">{anomaly.imo}</TableCell>
            <TableCell className="text-xs">{formatDateTime(anomaly.date)}</TableCell>
            <TableCell className="text-xs max-w-[200px] truncate" title={anomaly.reason}>
              {anomaly.reason}
            </TableCell>
            <TableCell>
              <SeverityBadge severity={anomaly.severity} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default function VesselAnomalyPage() {
  const { selectVessel } = useApp();
  const [activeTab, setActiveTab] = useState('invalid_mmsi');

  const anomalyStats = useMemo(() => ({
    invalid_mmsi: getAnomaliesByType('invalid_mmsi').length,
    invalid_imo: getAnomaliesByType('invalid_imo').length,
    invalid_callsign: getAnomaliesByType('invalid_callsign').length,
    stay_long_time: getAnomaliesByType('stay_long_time').length,
  }), []);

  const currentAnomalies = useMemo(() => 
    getAnomaliesByType(activeTab),
  [activeTab]);

  const handleVesselClick = (vessel) => {
    selectVessel(vessel);
  };

  return (
    <DashboardLayout title="Vessel Anomaly (AI)">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
        {/* Main Content - Anomaly Tables */}
        <div className="lg:col-span-2">
          <Card className="h-full flex flex-col">
            <CardHeader className="pb-0">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-destructive" />
                    Vessel Anomalies
                  </CardTitle>
                  <CardDescription>
                    AI-detected anomalies requiring verification
                  </CardDescription>
                </div>
                <Badge variant="outline" className="text-xs">
                  {anomalies.length} Total Issues
                </Badge>
              </div>
            </CardHeader>

            <CardContent className="flex-1 p-0 mt-4">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="w-full justify-start rounded-none border-b border-border bg-transparent px-6 h-12">
                  <TabsTrigger value="invalid_mmsi" className="text-xs gap-2">
                    <Hash className="w-3 h-3" />
                    Invalid MMSI
                    <Badge variant="secondary" className="ml-1 text-xs">{anomalyStats.invalid_mmsi}</Badge>
                  </TabsTrigger>
                  <TabsTrigger value="invalid_imo" className="text-xs gap-2">
                    <FileWarning className="w-3 h-3" />
                    Invalid IMO
                    <Badge variant="secondary" className="ml-1 text-xs">{anomalyStats.invalid_imo}</Badge>
                  </TabsTrigger>
                  <TabsTrigger value="invalid_callsign" className="text-xs gap-2">
                    <ShieldAlert className="w-3 h-3" />
                    Invalid Callsign
                    <Badge variant="secondary" className="ml-1 text-xs">{anomalyStats.invalid_callsign}</Badge>
                  </TabsTrigger>
                  <TabsTrigger value="stay_long_time" className="text-xs gap-2">
                    <Clock className="w-3 h-3" />
                    Stay Long Time
                    <Badge variant="secondary" className="ml-1 text-xs">{anomalyStats.stay_long_time}</Badge>
                  </TabsTrigger>
                </TabsList>

                <ScrollArea className="h-[calc(100vh-380px)]">
                  <TabsContent value="invalid_mmsi" className="m-0">
                    <AnomalyTable anomalies={currentAnomalies} onVesselClick={handleVesselClick} />
                  </TabsContent>
                  <TabsContent value="invalid_imo" className="m-0">
                    <AnomalyTable anomalies={currentAnomalies} onVesselClick={handleVesselClick} />
                  </TabsContent>
                  <TabsContent value="invalid_callsign" className="m-0">
                    <AnomalyTable anomalies={currentAnomalies} onVesselClick={handleVesselClick} />
                  </TabsContent>
                  <TabsContent value="stay_long_time" className="m-0">
                    <AnomalyTable anomalies={currentAnomalies} onVesselClick={handleVesselClick} />
                  </TabsContent>
                </ScrollArea>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Right Sidebar - AI Suggestions */}
        <div className="space-y-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 gap-3">
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">High Severity</p>
                  <p className="text-2xl font-semibold">
                    {anomalies.filter(a => a.severity === 'high').length}
                  </p>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-warning" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Medium</p>
                  <p className="text-2xl font-semibold">
                    {anomalies.filter(a => a.severity === 'medium').length}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* AI Suggestions Panel */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Brain className="w-4 h-4 text-primary" />
                AI Suggestions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 bg-accent/50 rounded-lg">
                <div className="flex items-start gap-2">
                  <ShieldAlert className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Potential Spoofing Detected</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      2 vessels with invalid MMSI patterns detected. Cross-reference with AIS provider recommended.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-accent/50 rounded-lg">
                <div className="flex items-start gap-2">
                  <FileWarning className="w-4 h-4 text-warning mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Data Quality Issue</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      IMO validation failures detected. Recommend verification with Lloyd's database.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-accent/50 rounded-lg">
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-info mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Extended Anchorage Alert</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      2 vessels have exceeded normal anchorage duration. Consider contacting vessel agents.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-3 border border-dashed border-border rounded-lg">
                <p className="text-xs text-muted-foreground text-center">
                  Recommend verification / cross-check with terrestrial AIS receiver for spoofing confirmation.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Actions Panel */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <button className="w-full p-3 text-left text-sm bg-muted/50 rounded-lg hover:bg-accent transition-colors">
                Export Anomaly Report
              </button>
              <button className="w-full p-3 text-left text-sm bg-muted/50 rounded-lg hover:bg-accent transition-colors">
                Send to Verification Queue
              </button>
              <button className="w-full p-3 text-left text-sm bg-muted/50 rounded-lg hover:bg-accent transition-colors">
                Configure Alert Thresholds
              </button>
            </CardContent>
          </Card>
        </div>
      </div>

      <VesselDrawer />
    </DashboardLayout>
  );
}
